import { Service, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environment/environment';
import { Authentification } from './authentification';
import { ReponseApi } from './api.types';
import { extraireReponse } from './api';
import { ErreurApi } from './api-error';
import {
  CategorieIntervention,
  creerBrouillonIntervention,
  InterventionEditable,
  NatureIntervention,
  peutSupprimerIntervention,
  SaisieIntervention,
} from './intervention-edition';

/** Métadonnées de l’API ; le binaire est téléchargé séparément avec authentification. */
export type PhotoIntervention = {
  id: number;
  nom: string;
  typeMime: string;
  taille: number;
  url: string;
};
type InterventionBaseApi = {
  id: number;
  installationId: number;
  description: string;
  auteurId: number;
  creeLe: string;
  modifieLe: string;
  /** Droit normalisé pour les vues, y compris lorsque l’API fournit peutModifier. */
  permissions: { modifier: boolean };
  peutModifier?: boolean;
  peutSupprimer?: boolean;
  peutTransformerEnBon?: boolean;
};
export type PlanificationApi = InterventionBaseApi & {
  nature: 'planification';
  categorie: CategorieIntervention;
  ticketId: number | null;
  date: string;
  debut: string | null;
  fin: string | null;
};
export type BonApi = InterventionBaseApi & {
  nature: 'bon';
  ticketId: number;
  problemeResolu: boolean;
  photos: PhotoIntervention[];
  ticket: { id: number; ferme: boolean };
};
export type InterventionApiResultat = PlanificationApi | BonApi;
/** La consultation historique peut ne fournir que les photos, sans nature ni droits d’édition. */
export type ConsultationIntervention = {
  photos: PhotoIntervention[];
  planification: PlanificationApi | null;
  /** Absente des réponses historiques sans identité vérifiable ; aucune suppression n’est alors proposée. */
  suppression?: InterventionEditable;
};

/** Reconstruit un brouillon depuis la réponse serveur, jamais depuis les libellés d’une carte. */
export function preparerEditionIntervention(
  intervention: InterventionApiResultat,
): InterventionEditable {
  const valeurs = {
    ...creerBrouillonIntervention({
      installationId: intervention.installationId,
      ...(intervention.ticketId ? { ticketId: intervention.ticketId } : {}),
    }),
    description: intervention.description,
  };
  if (intervention.nature === 'bon') valeurs.problemeResolu = intervention.problemeResolu;
  else {
    valeurs.categorie = intervention.categorie;
    valeurs.date = intervention.date;
    valeurs.debut = intervention.debut ?? '';
    valeurs.fin = intervention.fin ?? '';
  }
  return {
    id: intervention.id,
    nature: intervention.nature,
    auteurId: intervention.auteurId,
    valeurs,
  };
}

/** Garde de structure ; l’URL et le contenu binaire sont contrôlés au téléchargement. */
export function estPhotoIntervention(valeur: unknown): valeur is PhotoIntervention {
  if (!valeur || typeof valeur !== 'object') return false;
  const photo = valeur as Record<string, unknown>;
  return (
    typeof photo['id'] === 'number' &&
    Number.isSafeInteger(photo['id']) &&
    photo['id'] > 0 &&
    typeof photo['nom'] === 'string' &&
    typeof photo['url'] === 'string' &&
    typeof photo['typeMime'] === 'string' &&
    typeof photo['taille'] === 'number'
  );
}
type EnveloppeIntervention<T> = ReponseApi<T> & { erreursChamps?: Record<string, string> };
/** Refus métier conservant les messages par champ pour leur affichage dans le formulaire. */
export class ErreurIntervention extends ErreurApi {
  constructor(
    message: string,
    readonly champs: Record<string, string> = {},
    details?: string | string[],
    status?: number,
    cause?: unknown,
  ) {
    super(message, 'metier', details, status, cause);
  }
}

/** Adapte les saisies métier aux routes HTTP et exige une réponse serveur exploitable. */
@Service()
export class InterventionApi {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(Authentification);

  /** Lit une seule fois les photos et la nature réelle, sans déduire une planification de sa date. */
  async consulter(id: number, categorie: CategorieIntervention): Promise<ConsultationIntervention> {
    if (!Number.isSafeInteger(id) || id <= 0 || !['curative', 'preventive'].includes(categorie))
      throw new ErreurApi('Intervention non identifiée.', 'reponse_invalide');
    const res = await firstValueFrom(
      this.http.get<ReponseApi<InterventionApiResultat & { photos?: unknown }>>(
        `${this.route('planification')}/${categorie}/${id}`,
        this.auth.prepareHeaders(),
      ),
    );
    const objet = extraireReponse(res);
    if (!objet) throw new ErreurApi('Intervention indisponible.', 'reponse_invalide');
    const photos = objet.photos ?? (objet.nature === 'planification' ? [] : null);
    if (!Array.isArray(photos) || !photos.every(estPhotoIntervention))
      throw new ErreurApi('La liste des photos reçue est invalide.', 'reponse_invalide');
    const planification =
      objet.nature === 'planification'
        ? (this.resultat(objet, 'planification', id) as PlanificationApi)
        : null;
    if (planification && planification.categorie !== categorie)
      throw new ErreurApi(
        'La catégorie de la planification reçue ne correspond pas à la demande.',
        'reponse_invalide',
      );
    // Les anciens bons restent consultables même sans métadonnées complètes. Une action destructive
    // exige l’identité, l’auteur et le rattachement réels ; une simple liste de photos ne suffit pas.
    const bonIdentifie =
      objet.nature === 'bon' &&
      categorie === 'curative' &&
      objet.id === id &&
      Number.isSafeInteger(objet.installationId) &&
      objet.installationId > 0 &&
      Number.isSafeInteger(objet.auteurId) &&
      objet.auteurId > 0 &&
      Number.isSafeInteger(objet.ticketId) &&
      objet.ticketId > 0 &&
      typeof objet.description === 'string';
    const intervention =
      planification ??
      (bonIdentifie ? { ...objet, problemeResolu: objet.problemeResolu === true } : null);
    return {
      photos,
      planification,
      ...(intervention ? { suppression: preparerEditionIntervention(intervention) } : {}),
    };
  }

  /** DELETE sans corps : seul un 204 confirme la suppression, sans modifier le ticket associé. */
  async supprimer(intervention: InterventionEditable): Promise<void> {
    if (!peutSupprimerIntervention(this.auth.tokenInfoSignal(), intervention))
      throw new ErreurApi('Vous n’avez pas le droit de supprimer cette intervention.', 'metier');
    const route =
      intervention.nature === 'bon'
        ? this.route('bon')
        : `${this.route('planification')}/${intervention.valeurs.categorie}`;
    try {
      const reponse = await firstValueFrom(
        this.http.delete(`${route}/${intervention.id}`, {
          ...this.auth.prepareHeaders(),
          observe: 'response',
          responseType: 'text',
        }),
      );
      if (reponse.status !== 204)
        throw new ErreurApi(
          'La réponse ne confirme pas la suppression. Actualisez la liste avant de réessayer.',
          'reponse_invalide',
        );
    } catch (erreur) {
      const status =
        erreur instanceof ErreurApi || erreur instanceof HttpErrorResponse
          ? erreur.status
          : undefined;
      const messages: Record<number, string> = {
        401: 'Authentification requise. Reconnectez-vous pour supprimer cette intervention.',
        403: 'Vous n’avez pas le droit de supprimer cette intervention.',
        404: 'Cette intervention est absente, inaccessible ou ne correspond plus au type demandé. Actualisez la liste.',
        500: 'La suppression a échoué sur le serveur. Réessayez ultérieurement.',
      };
      if (status && messages[status])
        throw new ErreurApi(messages[status], 'http', undefined, status, erreur);
      throw erreur;
    }
  }

  /** Relit un bon, avec ses photos et ses permissions, avant une modification. */
  async lire(nature: 'bon', id: number): Promise<InterventionApiResultat>;
  /** Relit une planification dans sa catégorie actuelle et vérifie l’identité de la réponse. */
  async lire(
    nature: 'planification',
    id: number,
    categorie: CategorieIntervention,
  ): Promise<InterventionApiResultat>;
  async lire(
    nature: NatureIntervention,
    id: number,
    categorie?: CategorieIntervention,
  ): Promise<InterventionApiResultat> {
    if (nature === 'planification' && !categorie)
      throw new Error('Catégorie de planification requise.');
    const route =
      nature === 'planification' ? `${this.route(nature)}/${categorie}` : this.route(nature);
    const res = await firstValueFrom(
      this.http.get<ReponseApi<InterventionApiResultat>>(
        `${route}/${id}`,
        this.auth.prepareHeaders(),
      ),
    );
    const resultat = this.resultat(extraireReponse(res), nature, id);
    if (resultat.nature === 'planification' && resultat.categorie !== categorie) {
      throw new ErreurApi(
        'La catégorie de la planification reçue ne correspond pas à la demande.',
        'reponse_invalide',
      );
    }
    return resultat;
  }

  /**
   * Crée (POST) ou modifie (PUT) et ne résout qu’après confirmation métier de l’API.
   * En création, l’objet peut être renvoyé directement ou dans une enveloppe statut/reponse.
   * La nature du résultat vient de la saisie, indépendamment du champ renvoyé par le serveur.
   * En modification, categorieOrigine identifie la route même si la saisie change de catégorie.
   */
  async enregistrer(
    saisie: SaisieIntervention,
    categorieOrigine?: CategorieIntervention,
  ): Promise<InterventionApiResultat> {
    const body = this.corpsEnregistrement(saisie);
    const route =
      saisie.nature === 'planification' && saisie.id !== null
        ? `${this.route(saisie.nature)}/${categorieOrigine ?? saisie.categorie}`
        : this.route(saisie.nature);
    const url = route + (saisie.id === null ? '' : `/${saisie.id}`);
    const methode = saisie.id === null ? 'POST' : 'PUT';
    try {
      const res = await firstValueFrom(
        this.http.request<InterventionApiResultat | EnveloppeIntervention<InterventionApiResultat>>(
          methode,
          url,
          {
            ...this.auth.prepareHeaders(),
            body,
          },
        ),
      );
      let objet: InterventionApiResultat | null;
      if (res && typeof res === 'object' && 'statut' in res) {
        // Un refus métier explicite reste prioritaire, même avec un succès HTTP.
        if (typeof res.statut !== 'string' || res.statut.trim().toUpperCase() !== 'OK')
          throw this.erreurValidation(res);
        objet = res.reponse ?? null;
      } else {
        // Les PUT exigent une enveloppe ; les POST acceptent aussi un objet direct.
        if (saisie.id !== null) throw this.erreurValidation(res);
        objet = res;
      }
      // Le backend peut qualifier une planification de « bon ». Seul ce discriminant est remplacé ;
      // resultat vérifie ensuite tous les champs requis pour l’opération demandée.
      const normalise = objet
        ? ({ ...objet, nature: saisie.nature } as InterventionApiResultat)
        : null;
      const resultat = this.resultat(normalise, saisie.nature, saisie.id ?? undefined);
      // La création préventive peut générer un ticket que la saisie ne connaissait pas.
      const ticketCreeParPlanification =
        saisie.id === null &&
        saisie.nature === 'planification' &&
        saisie.categorie === 'preventive' &&
        saisie.ticketId === null;
      // Une réponse d’un autre contexte ne doit pas fermer la saisie ni actualiser une autre vue.
      if (
        resultat.installationId !== saisie.installationId ||
        (!ticketCreeParPlanification && resultat.ticketId !== saisie.ticketId) ||
        (saisie.nature === 'planification' &&
          resultat.nature === 'planification' &&
          resultat.categorie !== saisie.categorie)
      ) {
        throw new ErreurApi(
          'L’intervention reçue ne correspond pas à la centrale, au ticket ou à la catégorie demandés.',
          'reponse_invalide',
          'Vérifiez les interventions existantes avant de réessayer : l’enregistrement peut avoir été effectué.',
        );
      }
      return resultat;
    } catch (erreur) {
      // L’intercepteur peut déjà avoir converti l’erreur HTTP.
      const http = erreur instanceof ErreurApi ? erreur.cause : erreur;
      if (http instanceof HttpErrorResponse && http.status >= 400 && http.status < 500) {
        throw this.erreurValidation(http.error, http);
      }
      throw erreur;
    }
  }

  /** Racine distincte des bons ; la catégorie des planifications est ajoutée par l’appelant. */
  private route(nature: NatureIntervention): string {
    return `${environment.apiUrl}/intervention${nature === 'bon' ? '/bon' : ''}`;
  }

  /** Construit les champs du contrat sans fuseau, en JSON comme dans la partie donnees du multipart. */
  private corpsEnregistrement(saisie: SaisieIntervention) {
    const donnees =
      saisie.nature === 'planification'
        ? {
            categorie: saisie.categorie,
            installationId: saisie.installationId,
            ticketId: saisie.ticketId,
            date: saisie.date,
            debut: saisie.debut || null,
            fin: saisie.fin || null,
            description: saisie.description,
          }
        : {
            installationId: saisie.installationId,
            ticketId: saisie.ticketId,
            description: saisie.description,
            problemeResolu: saisie.problemeResolu,
          };
    // Sans fichier, envoyer du JSON. Avec photos, laisser le navigateur définir la boundary
    // multipart ; l’ordre de photos[] correspond aux indices des erreurs de validation.
    if (saisie.nature !== 'bon' || !saisie.nouvellesPhotos.length) return donnees;
    const body = new FormData();
    body.append('donnees', JSON.stringify(donnees));
    for (const photo of saisie.nouvellesPhotos) body.append('photos[]', photo, photo.name);
    return body;
  }

  /** Extrait les messages fiables d’un corps inconnu et précise les refus de taille ou de format. */
  private erreurValidation(corps: unknown, http?: HttpErrorResponse): ErreurIntervention {
    const objet = corps && typeof corps === 'object' ? (corps as Record<string, unknown>) : {};
    const champs = objet['erreursChamps'];
    const details = objet['details'];
    const message = objet['message'];
    const repli =
      http?.status === 413
        ? 'Les fichiers envoyés sont trop volumineux. Réduisez leur taille ou leur nombre.'
        : http?.status === 415
          ? 'Le format de l’envoi est refusé. Pour les photos, utilisez des images JPEG, PNG ou WebP.'
          : 'Enregistrement refusé.';
    return new ErreurIntervention(
      typeof message === 'string' && message.trim() ? message.trim() : repli,
      champs && typeof champs === 'object' && !Array.isArray(champs)
        ? Object.fromEntries(
            Object.entries(champs)
              .filter((e): e is [string, string] => typeof e[1] === 'string' && !!e[1].trim())
              .map(([champ, message]) => [champ, message.trim()]),
          )
        : {},
      typeof details === 'string'
        ? details
        : Array.isArray(details)
          ? details.filter((detail): detail is string => typeof detail === 'string')
          : undefined,
      http?.status,
      http,
    );
  }

  /** Refuse une réponse incohérente avant qu’elle déclenche fermeture et actualisation des vues. */
  private resultat(
    objet: InterventionApiResultat | null,
    nature: NatureIntervention,
    id?: number,
  ): InterventionApiResultat {
    // Le contrat récent expose les droits à plat ; conserver le modèle utilisé par les vues.
    if (objet?.nature === 'planification' && typeof objet.peutModifier === 'boolean') {
      objet = { ...objet, permissions: { modifier: objet.peutModifier } };
    }
    if (
      !objet ||
      !Number.isSafeInteger(objet.id) ||
      objet.id <= 0 ||
      objet.nature !== nature ||
      (id !== undefined && objet.id !== id) ||
      !Number.isSafeInteger(objet.installationId) ||
      objet.installationId <= 0 ||
      typeof objet.description !== 'string' ||
      !Number.isSafeInteger(objet.auteurId) ||
      objet.auteurId <= 0 ||
      (objet.ticketId !== null && (!Number.isSafeInteger(objet.ticketId) || objet.ticketId <= 0)) ||
      !objet.permissions ||
      typeof objet.permissions.modifier !== 'boolean' ||
      (objet.nature === 'bon' &&
        (!Array.isArray(objet.photos) ||
          !objet.photos.every(estPhotoIntervention) ||
          typeof objet.ticket?.ferme !== 'boolean' ||
          objet.ticket.id !== objet.ticketId ||
          typeof objet.problemeResolu !== 'boolean' ||
          objet.ticketId === null)) ||
      (objet.nature === 'planification' &&
        (!['curative', 'preventive'].includes(objet.categorie) ||
          typeof objet.date !== 'string' ||
          (objet.debut != null && typeof objet.debut !== 'string') ||
          (objet.fin != null && typeof objet.fin !== 'string')))
    ) {
      throw new ErreurApi(
        'La réponse ne confirme pas l’intervention enregistrée.',
        'reponse_invalide',
        'Vérifiez les interventions existantes avant de réessayer : l’enregistrement peut avoir été effectué.',
      );
    }
    // Tolérer les heures absentes des anciennes réponses et les normaliser pour le formulaire.
    return objet.nature === 'planification'
      ? { ...objet, debut: objet.debut || null, fin: objet.fin || null }
      : objet;
  }
}
